import { Component } from '@theme/component';
import { CartItemsComponent } from '@theme/component-cart-items';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that adds or removes the gift wrap product from the cart.
 *
 * The checkbox's checked state is rendered by Liquid from the cart contents, so removing the
 * gift wrap line with the cart's own remove button unchecks the box when the section morphs.
 *
 * @typedef {Object} CartGiftWrapComponentRefs
 * @property {HTMLInputElement} giftWrapCheckbox - The gift wrap checkbox.
 */

/**
 * @extends {Component<CartGiftWrapComponentRefs>}
 */
class CartGiftWrap extends Component {
  requiredRefs = ['giftWrapCheckbox'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #pending = false;

  /**
   * Keeps the checkbox focusable while the pending guard prevents another mutation.
   * aria-busy swaps the checkbox for a spinner, and the cart is locked with the same class and
   * total shimmer that quantity updates use, so checkout and line edits wait for the update.
   * @returns {() => void} Restores the controls' previous states.
   */
  #disableControls() {
    const { giftWrapCheckbox } = this.refs;
    const cartItems = this.closest('cart-items-component');
    const previousBusy = this.getAttribute('aria-busy');
    const previousDisabled = giftWrapCheckbox.getAttribute('aria-disabled');
    const previousCartDisabled = cartItems?.classList.contains('cart-items-disabled') ?? false;
    const cartTotal = /** @type {TextComponent | null | undefined} */ (
      cartItems?.querySelector('text-component[ref="cartTotal"]')
    );

    this.setAttribute('aria-busy', 'true');
    giftWrapCheckbox.setAttribute('aria-disabled', 'true');
    cartItems?.classList.add('cart-items-disabled');
    cartTotal?.shimmer();

    return () => {
      if (previousBusy === null) this.removeAttribute('aria-busy');
      else this.setAttribute('aria-busy', previousBusy);

      if (previousDisabled === null) giftWrapCheckbox.removeAttribute('aria-disabled');
      else giftWrapCheckbox.setAttribute('aria-disabled', previousDisabled);

      if (!previousCartDisabled) cartItems?.classList.remove('cart-items-disabled');
      cartTotal?.removeAttribute('shimmer');
    };
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Every cart-items-component on the page (drawer and cart page) needs the new cart rendered.
   * @returns {string[]} The section ids to request with the cart update.
   */
  #sectionsToUpdate() {
    const sectionIds = new Set(this.dataset.sectionId ? [this.dataset.sectionId] : []);
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    }

    return Array.from(sectionIds);
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * Cancelled renders can resolve without validation, so retry until this refresh reaches the DOM.
   */
  async #refreshGiftWrapSection() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    let validated = false;

    try {
      while (!validated && this.isConnected) {
        let pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
        while (pendingQuantityUpdates.length) {
          await Promise.all(pendingQuantityUpdates);
          pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
        }
        if (!this.isConnected) return;

        await sectionRenderer.renderSection(sectionId, {
          cache: false,
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
          shouldRender: () => {
            validated = true;
            return true;
          },
        });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart gift wrap',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /**
   * Adds the gift wrap product when the box is checked, removes it when unchecked.
   * @param {Event} event - The change event on the checkbox.
   */
  toggleGiftWrap = async (event) => {
    event.stopPropagation();

    const { giftWrapCheckbox } = this.refs;
    const variantId = this.dataset.gift;
    const sectionId = this.dataset.sectionId;

    if (this.#pending || !variantId || typeof sectionId !== 'string') {
      giftWrapCheckbox.checked = !giftWrapCheckbox.checked;
      return;
    }

    const quantity = giftWrapCheckbox.checked ? 1 : 0;
    const sectionsToUpdate = this.#sectionsToUpdate();

    for (const id of sectionsToUpdate) {
      sectionRenderer.abortRender(id);
    }
    const abortController = this.#createAbortController();
    const deferredPromise = CartLinesUpdateEvent.createPromise();

    this.#pending = true;
    const restoreControls = this.#disableControls();

    try {
      // Dispatch from the parent cart-items-component so it skips its own re-render (we morph
      // it below) while any other cart-items-component and the cart icon still update.
      const eventTarget = this.closest('cart-items-component') ?? this;
      eventTarget.dispatchEvent(
        quantity > 0
          ? new CartLinesUpdateEvent({
              action: 'add',
              context: 'cart',
              lines: [{ merchandiseId: variantId, quantity }],
              promise: deferredPromise.promise,
            })
          : new CartLinesUpdateEvent({
              action: 'remove',
              context: 'cart',
              lines: [{ id: this.dataset.giftLineKey ?? '', quantity }],
              promise: deferredPromise.promise,
            })
      );

      // Keyed by variant id: adds the line when it isn't in the cart, and setting 0 removes it.
      const config = fetchConfig('json', {
        body: JSON.stringify({
          updates: { [variantId]: quantity },
          sections: sectionsToUpdate,
          sections_url: window.location.pathname,
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (!response.ok) {
        giftWrapCheckbox.checked = !giftWrapCheckbox.checked;
        const error = new Error(data.description || data.message || 'Failed to update gift wrap');
        deferredPromise.reject(error);
        this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
        return;
      }

      const sections = /** @type {Record<string, string> | null | undefined} */ (data.sections);
      const newHtml = sections?.[sectionId];

      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: sections ?? undefined,
          items: data.items,
          itemCount: data.item_count,
          source: 'cart-gift-wrap',
          didError: false,
        },
      });

      if (!newHtml) {
        await this.#refreshGiftWrapSection();
        return;
      }

      sectionRenderer.abortRender(sectionId);
      await morphSection(sectionId, newHtml, {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        giftWrapCheckbox.checked = !giftWrapCheckbox.checked;
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to update gift wrap',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      this.#pending = false;
      restoreControls();
    }
  };
}

if (!customElements.get('cart-gift-wrap')) {
  customElements.define('cart-gift-wrap', CartGiftWrap);
}
